/**
 * ✅ REQUIERE DEPENDENCIAS (una sola vez):
 * npm install jspdf html2canvas
 */

import React, { useEffect, useMemo, useState } from "react";
import { REPORTES_API } from "../api/reportesApi";
import { calcularVariacionMunicipios } from "../utils/calcularVariacionMunicipios";
import { calcularResumenMunicipios } from "../utils/calcularResumenMunicipios";
import { analizarMunicipios } from "../utils/analizarMunicipio";
import LoaderPremiumLogo from "../components/LoaderPremiumLogo";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* ===============================
   MESES
================================ */
const MESES = {
  "01": "Enero",
  "02": "Febrero",
  "03": "Marzo",
  "04": "Abril",
  "05": "Mayo",
  "06": "Junio",
  "07": "Julio",
  "08": "Agosto",
  "09": "Septiembre",
  "10": "Octubre",
  "11": "Noviembre",
  "12": "Diciembre",
};

const nombreMes = (mes) => MESES[mes?.toString().padStart(2, "0")] || mes;

/* ===============================
   HEATMAP COLORS
================================ */
const colorHeatmap = (variacion) => {
  if (variacion <= -15) return "bg-red-600 text-white";
  if (variacion < 0) return "bg-red-300";
  if (variacion < 10) return "bg-yellow-200";
  if (variacion < 20) return "bg-green-300";
  return "bg-green-600 text-white";
};

export default function VariacionMunicipiosMensual() {
  const [anio, setAnio] = useState(2025);
  const [loading, setLoading] = useState(false);

  const [filas, setFilas] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [insights, setInsights] = useState([]);
  const [comparativo, setComparativo] = useState([]);

  // ✅ C) Accordion
  const [mesAbierto, setMesAbierto] = useState(null);

  // ✅ E) Modo Ejecutivo / Técnico
  const [modo, setModo] = useState("EJECUTIVO"); // EJECUTIVO | TECNICO

  // ✅ PDF Ejecutivo
  const [exportandoPDF, setExportandoPDF] = useState(false);

  // ✅ Resumen IA mensual
  const [mesResumen, setMesResumen] = useState("01");
  const [resumenIA, setResumenIA] = useState("");
  const [resumenIAFuente, setResumenIAFuente] = useState(""); // "IA" | "LOCAL"
  const [loadingIA, setLoadingIA] = useState(false);
  const [errorIA, setErrorIA] = useState("");

  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line
  }, []);

  const cargarTodo = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${REPORTES_API}/reportes/municipios/variacion?anio=${anio}`
      );
      if (!res.ok) throw new Error("Error backend");

      const json = await res.json();
      const filasCalc = calcularVariacionMunicipios(json);

      setFilas(filasCalc);
      setResumen(calcularResumenMunicipios(filasCalc));
      setInsights(analizarMunicipios(filasCalc));

      const [res24, res25] = await Promise.all([
        fetch(`${REPORTES_API}/reportes/municipios/variacion?anio=2024`),
        fetch(`${REPORTES_API}/reportes/municipios/variacion?anio=2025`),
      ]);

      const data24 = calcularVariacionMunicipios(await res24.json());
      const data25 = calcularVariacionMunicipios(await res25.json());

      setComparativo(generarComparativo(data24, data25));

      // ✅ Abre el primer mes disponible automáticamente (solo si aún no hay uno abierto)
      setTimeout(() => {
        setMesAbierto((prev) => prev ?? (filasCalc?.[0]?.mes || null));
      }, 0);

      // ✅ Ajusta mesResumen al primer mes disponible (si aplica)
      const meses = Array.from(new Set(filasCalc.map((f) => f.mes))).sort();
      setMesResumen((prev) => (meses.includes(prev) ? prev : meses[0] || "01"));

      // Resetea resumen IA al recargar datos
      setResumenIA("");
      setResumenIAFuente("");
      setErrorIA("");
    } catch (e) {
      console.error("❌ Error cargando reportes:", e);
      setFilas([]);
      setResumen([]);
      setInsights([]);
      setComparativo([]);
      setMesAbierto(null);
      setResumenIA("");
      setResumenIAFuente("");
      setErrorIA("");
    } finally {
      setLoading(false);
    }
  };

  const mesesDisponibles = useMemo(() => {
    return Array.from(new Set(filas.map((f) => f.mes))).sort();
  }, [filas]);

  const municipios = useMemo(() => {
    return Array.from(new Set(filas.map((f) => f.municipio))).sort();
  }, [filas]);

  const heatmap = useMemo(() => {
    return municipios.map((mun) => {
      const row = { municipio: mun };
      mesesDisponibles.forEach((mes) => {
        const dato = filas.find((f) => f.municipio === mun && f.mes === mes);
        row[mes] = dato ? dato.variacion : null;
      });
      return row;
    });
  }, [municipios, mesesDisponibles, filas]);

  /* ===============================
     TOP 5 (DERIVADO DE COMPARATIVO)
  ================================ */
 const topMejoraron = useMemo(() => {
  return [...comparativo]
    .filter((c) => Number(c.delta) > 0)
    .sort((a, b) => Number(b.delta) - Number(a.delta))
    .slice(0, 5);
}, [comparativo]);

const topCayeron = useMemo(() => {
  return [...comparativo]
    .filter((c) => Number(c.delta) < 0 && Number(c.total2024) > 0) // 👈 caída real: había base en 2024
    .sort((a, b) => Number(a.delta) - Number(b.delta))
    .slice(0, 5);
}, [comparativo]);


  /* ===============================
     KPIs EJECUTIVOS
  ================================ */
  const totalMunicipios = resumen.length;

  const municipiosEnRiesgo = resumen.filter(
    (r) => r.tendencia === "DECRECIENTE"
  ).length;

  const municipiosCreciente = resumen.filter(
    (r) => r.tendencia === "CRECIENTE"
  ).length;

  const municipiosEstables = resumen.filter(
    (r) => r.tendencia === "ESTABLE"
  ).length;

  let estadoGeneral = "⚪ Estable";
  if (municipiosEnRiesgo > 2) estadoGeneral = "🔴 Riesgo alto";
  else if (municipiosEnRiesgo > 0) estadoGeneral = "🟠 Atención";
  else if (municipiosCreciente > municipiosEstables)
    estadoGeneral = "🟢 Tendencia positiva";

  /* ===============================
     PDF EJECUTIVO (FIX: MULTI-PÁGINA REAL)
  ================================ */
  const exportarPDFEjecutivo = async () => {
    try {
      setExportandoPDF(true);

      const element = document.getElementById("pdf-ejecutivo");
      if (!element) {
        alert("No se encontró el contenedor ejecutivo");
        return;
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // PRIMERA PÁGINA
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // PÁGINAS SIGUIENTES
      while (heightLeft > 0) {
        pdf.addPage();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `Variacion_Municipal_Ejecutivo_${anio}_${nombreMes(mesResumen)}.pdf`
      );
    } catch (error) {
      console.error("❌ Error al exportar PDF:", error);
      alert("Error al generar el PDF. Revisa la consola.");
    } finally {
      setExportandoPDF(false);
    }
  };

  /* ===============================
     RESUMEN IA MENSUAL (Bedrock/IA via Backend)
     - Intenta IA primero
     - Si falla, usa resumen local premium (NO rompe)
  ================================ */
  const generarResumenIAMensual = async () => {
    try {
      setLoadingIA(true);
      setErrorIA("");
      setResumenIA("");
      setResumenIAFuente("");

      const payload = buildResumenPayload({
        anio,
        mesResumen,
        estadoGeneral,
        totalMunicipios,
        municipiosCreciente,
        municipiosEstables,
        municipiosEnRiesgo,
        topMejoraron,
        topCayeron,
        insights,
        resumen,
      });

      const res = await fetch(`${REPORTES_API}/reportes/municipios/resumen-ia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Backend IA no disponible (status no-ok)");
      }

      const json = await res.json();
      const texto =
        json?.resumen ||
        json?.text ||
        json?.message ||
        json?.output ||
        "";

      if (!texto || typeof texto !== "string") {
        throw new Error("Respuesta IA inválida");
      }

      setResumenIA(texto.trim());
      setResumenIAFuente("IA");
    } catch (e) {
      console.warn("⚠️ IA no disponible, usando resumen local:", e);

      const local = generarResumenLocal({
        anio,
        mesResumen,
        estadoGeneral,
        totalMunicipios,
        municipiosCreciente,
        municipiosEstables,
        municipiosEnRiesgo,
        topMejoraron,
        topCayeron,
        insights,
      });

      setResumenIA(local);
      setResumenIAFuente("LOCAL");
      setErrorIA(
        "No se pudo obtener IA desde backend. Se generó un resumen ejecutivo local como respaldo."
      );
    } finally {
      setLoadingIA(false);
    }
  };

  return (
    <div className="p-6 space-y-10 relative">
      {/* 🔄 LOADER GLOBAL PREMIUM */}
      {loading && <LoaderPremiumLogo />}

      {/* ===============================
          TOGGLE MODO EJECUTIVO / TÉCNICO
      ================================ */}
      <div className="flex justify-end">
        <div className="inline-flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setModo("EJECUTIVO")}
            className={`px-4 py-1.5 text-sm rounded-md transition ${
              modo === "EJECUTIVO"
                ? "bg-blue-900 text-white"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            Ejecutivo
          </button>

          <button
            onClick={() => setModo("TECNICO")}
            className={`px-4 py-1.5 text-sm rounded-md transition ${
              modo === "TECNICO"
                ? "bg-blue-900 text-white"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            Técnico
          </button>
        </div>
      </div>

      {/* ======================================================
          ✅ CONTENEDOR EJECUTIVO (PDF TOMA ESTE BLOQUE)
      ======================================================= */}
      <div id="pdf-ejecutivo" className="space-y-6">
        {/* ===============================
            HEADER EJECUTIVO
        ================================ */}
        <div className="bg-white p-6 rounded-xl shadow space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                 Variación Municipal — {anio}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Periodo:{" "}
                <span className="font-semibold">
                  {nombreMes(mesResumen)} — {anio}
                </span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Estado general:{" "}
                <span className="font-semibold">{estadoGeneral}</span>
              </p>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="number"
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                className="border px-3 py-2 rounded w-28"
              />

              <select
                value={mesResumen}
                onChange={(e) => setMesResumen(e.target.value)}
                className="border px-3 py-2 rounded"
              >
                {(mesesDisponibles.length ? mesesDisponibles : Object.keys(MESES))
                  .sort()
                  .map((m) => (
                    <option key={m} value={m}>
                      {nombreMes(m)}
                    </option>
                  ))}
              </select>

              <button
                onClick={cargarTodo}
                className="bg-blue-900 hover:bg-blue-800 text-white px-4 py-2 rounded transition"
              >
                Actualizar
              </button>

              <button
                onClick={exportarPDFEjecutivo}
                disabled={exportandoPDF}
                className={`px-4 py-2 rounded transition text-white ${
                  exportandoPDF
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-900 hover:bg-gray-800"
                }`}
              >
                {exportandoPDF ? "📄 Exportando..." : "📄 Exportar PDF Ejecutivo"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-500">Municipios</p>
              <p className="text-xl font-bold">{totalMunicipios}</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-xs text-green-700">🟢 En crecimiento</p>
              <p className="text-xl font-bold text-green-800">
                {municipiosCreciente}
              </p>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-xs text-red-700">🔴 En riesgo</p>
              <p className="text-xl font-bold text-red-800">
                {municipiosEnRiesgo}
              </p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-xs text-yellow-700">⚪ Estables</p>
              <p className="text-xl font-bold text-yellow-800">
                {municipiosEstables}
              </p>
            </div>
          </div>
        </div>

        {/* ===============================
            TOP 5 – RANKING VISUAL
        ================================ */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* 🟢 MEJORARON */}
          <div className="bg-white p-5 rounded shadow">
            <h2 className="font-bold text-green-700 mb-4 flex items-center gap-2">
              🟢 Top 5 Municipios que Mejoraron (2024 → 2025)
            </h2>

            <div className="space-y-3 text-sm">
              {topMejoraron.length === 0 ? (
                <div className="text-gray-500 text-sm">
                  No hay municipios con mejora para mostrar.
                </div>
              ) : (
                topMejoraron.map((m) => {
                  const pct = m.porcentaje; // puede ser null
                  const width = pct === null ? 100 : Math.min(Math.abs(Number(pct) || 0), 100);


                  return (
                    <div key={m.municipio}>
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">{m.municipio}</span>
                        <span className="font-semibold text-green-700">
                          <span className="font-semibold text-green-700">
                          {pct === null ? "Nuevo" : `+${pct}%`}
                          </span>

                        </span>
                      </div>

                      <div className="w-full bg-green-100 rounded h-2 overflow-hidden">
                        <div
                          className="bg-green-600 h-2 rounded transition-all"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* 🔴 CAYERON */}
          <div className="bg-white p-5 rounded shadow">
            <h2 className="font-bold text-red-700 mb-4 flex items-center gap-2">
              🔴 Top 5 Municipios que Cayeron (2024 → 2025)
            </h2>

            <div className="space-y-3 text-sm">
              {topCayeron.length === 0 ? (
                <div className="text-gray-500 text-sm">
                  No hay municipios con caída para mostrar.
                </div>
              ) : (
              topCayeron.map((m) => {
  const pct = m.porcentaje; // null | number
  const width = pct === null ? 100 : Math.min(Math.abs(Number(pct) || 0), 100);

  return (
    <div key={m.municipio}>
      <div className="flex justify-between mb-1">
        <span className="font-medium">{m.municipio}</span>
        <span className="font-semibold text-red-700">
          {pct === null ? "N/A" : `${pct}%`}
        </span>
      </div>

      <div className="w-full bg-red-100 rounded h-2 overflow-hidden">
        <div
          className="bg-red-600 h-2 rounded transition-all"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
})

              )}
            </div>
          </div>
        </div>

        {/* ===============================
            RESUMEN IA MENSUAL
        ================================ */}
        <div className="bg-white p-6 rounded-xl shadow space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
               Resumen Ejecutivo Mensual
              {resumenIAFuente ? (
                <span className="text-xs font-semibold text-gray-500">
                  ({resumenIAFuente === "IA" ? "IA" : "Respaldo local"})
                </span>
              ) : null}
            </h2>

            <button
              onClick={generarResumenIAMensual}
              disabled={loadingIA}
              className={`px-4 py-2 rounded transition text-white ${
                loadingIA
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-purple-700 hover:bg-purple-600"
              }`}
            >
              {loadingIA ? "Generando..." : "Generar resumen IA"}
            </button>
          </div>

          {errorIA ? (
            <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
              {errorIA}
            </div>
          ) : null}

          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {resumenIA
              ? resumenIA
              : "Genera un resumen mensual tipo informe (directivo) con base en KPIs, Top 5 e insights."}
          </div>

          <div className="text-xs text-gray-500">
            Periodo: {nombreMes(mesResumen)} — {anio}
          </div>
        </div>

        {/* ===============================
            INSIGHTS ACCIONABLES (Recomendaciones)
        ================================ */}
        <div className="bg-white p-6 rounded-xl shadow space-y-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            Insights Accionables (Recomendaciones)
          </h2>

          {insights.map((i) => {
            const info = generarInsightAccionable(i);

            return (
              <div
                key={`accion-${i.municipio}`}
                className={`border-l-4 p-4 rounded ${info.color}`}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-sm">{i.municipio}</h3>
                  <span className="text-xs font-bold">Riesgo: {info.nivel}</span>
                </div>

                <p className="text-sm mt-1">{i.insight}</p>

                <p className="text-sm mt-2 font-semibold">Acción sugerida:</p>
                <p className="text-sm">{info.accion}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===============================
          ✅ MODO TÉCNICO: DETALLE COMPLETO
      ================================ */}
      {modo === "TECNICO" && (
        <>
          {/* TABLAS POR MES (ACCORDION) */}
          {mesesDisponibles.map((mes) => {
            const abierto = mesAbierto === mes;

            return (
              <div key={mes} className="bg-white rounded shadow overflow-hidden">
                <button
                  onClick={() => setMesAbierto(abierto ? null : mes)}
                  className="w-full flex justify-between items-center px-5 py-4 bg-gray-50 hover:bg-gray-100 transition"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold">
                      {nombreMes(mes)} — {anio}
                    </span>
                  </div>

                  <span className="text-xl">{abierto ? "▾" : "▸"}</span>
                </button>

                {abierto && (
                  <div className="p-5 space-y-4">
                    <table className="w-full border text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border p-2 text-left">Municipio</th>
                          <th className="border p-2 text-right">Valor</th>
                          <th className="border p-2 text-right">Δ %</th>
                          <th className="border p-2 text-center">Semáforo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filas
                          .filter((f) => f.mes === mes)
                          .map((f) => (
                            <tr key={`${mes}-${f.municipio}`}>
                              <td className="border p-2">{f.municipio}</td>
                              <td className="border p-2 text-right">
                                {f.valor.toLocaleString("es-MX")}
                              </td>
                              <td className="border p-2 text-right">
                                {f.variacion}%
                              </td>
                              <td className="border p-2 text-center">
                                {f.semaforo}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* HEATMAP */}
          <div className="bg-white p-5 rounded shadow">
            <h2 className="text-lg font-bold mb-4">
              Heatmap Mensual por Municipio
            </h2>

            <div className="overflow-auto">
              <table className="border text-sm w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left">Municipio</th>
                    {mesesDisponibles.map((mes) => (
                      <th key={mes} className="border p-2 text-center">
                        {nombreMes(mes)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmap.map((row) => (
                    <tr key={row.municipio}>
                      <td className="border p-2 font-medium">{row.municipio}</td>
                      {mesesDisponibles.map((mes) => {
                        const val = row[mes];
                        return (
                          <td
                            key={mes}
                            className={`border p-2 text-center ${
                              val !== null ? colorHeatmap(val) : "bg-gray-100"
                            }`}
                          >
                            {val !== null ? `${val}%` : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* COMPARATIVO */}
          <div className="bg-white p-5 rounded shadow">
            <h2 className="text-lg font-bold mb-4">
              Comparación Anual 2024 vs 2025
            </h2>

            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2">Municipio</th>
                  <th className="border p-2 text-right">2024</th>
                  <th className="border p-2 text-right">2025</th>
                  <th className="border p-2 text-right">Δ</th>
                  <th className="border p-2 text-right">Δ %</th>
                  <th className="border p-2 text-center">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {comparativo.map((c) => (
                  <tr key={c.municipio}>
                    <td className="border p-2">{c.municipio}</td>
                    <td className="border p-2 text-right">
                      {Number(c.total2024 || 0).toLocaleString("es-MX")}
                    </td>
                    <td className="border p-2 text-right">
                      {Number(c.total2025 || 0).toLocaleString("es-MX")}
                    </td>
                    <td className="border p-2 text-right">
                      {Number(c.delta || 0).toLocaleString("es-MX")}
                    </td>
                    <td className="border p-2 text-right">{c.porcentaje}%</td>
                    <td className="border p-2 text-center">
                      {Number(c.delta) > 0 && "🟢 Mejoró"}
                      {Number(c.delta) === 0 && "🟡 Sin cambio"}
                      {Number(c.delta) < 0 && "🔴 Empeoró"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* RESUMEN (TÉCNICO) */}
          <div className="bg-white p-5 rounded shadow">
            <h2 className="text-lg font-bold mb-4">Resumen Ejecutivo</h2>

            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2">Municipio</th>
                  <th className="border p-2 text-right">Total</th>
                  <th className="border p-2 text-right">Prom. Variación</th>
                  <th className="border p-2 text-center">Tendencia</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r) => (
                  <tr key={r.municipio}>
                    <td className="border p-2">{r.municipio}</td>
                    <td className="border p-2 text-right">
                      {r.total.toLocaleString("es-MX")}
                    </td>
                    <td className="border p-2 text-right">
                      {r.promedioVariacion}%
                    </td>
                    <td className="border p-2 text-center">{r.tendencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* INSIGHTS (TÉCNICO) */}
          <div className="bg-white p-5 rounded shadow space-y-4">
            <h2 className="text-lg font-bold">Insights Automáticos</h2>

            {insights.map((i) => (
              <div
                key={i.municipio}
                className="border-l-4 p-4 rounded bg-gray-50"
              >
                <h3 className="font-semibold">{i.municipio}</h3>
                <p className="text-sm mt-1">{i.insight}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ===============================
   HELPERS
================================ */
function generarComparativo(data24, data25) {
  const totales = {};

  const normalizarMunicipio = (x = "") => {
    return String(x)
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // quita acentos
  };

  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const acumular = (data, key) => {
    (data || []).forEach((d) => {
      const munNorm = normalizarMunicipio(d.municipio);

      if (!totales[munNorm]) {
        totales[munNorm] = {
          municipio: munNorm, // usamos el normalizado como clave y display (evita duplicados)
          total2024: 0,
          total2025: 0,
        };
      }

      totales[munNorm][key] += toNumber(d.valor);
    });
  };

  acumular(data24, "total2024");
  acumular(data25, "total2025");

  return Object.values(totales).map((t) => {
    const total2024 = toNumber(t.total2024);
    const total2025 = toNumber(t.total2025);
    const delta = total2025 - total2024;

    // porcentaje: null cuando 2024=0 y 2025>0 (caso "Nuevo")
    let porcentaje = 0;
    if (total2024 === 0 && total2025 > 0) porcentaje = null;
    else if (total2024 > 0) porcentaje = Number(((delta / total2024) * 100).toFixed(2));

    return {
      municipio: t.municipio,
      total2024,
      total2025,
      delta,
      porcentaje,
    };
  });
}


function generarInsightAccionable(i) {
  if (i.tendencia === "DECRECIENTE") {
    return {
      nivel: "ALTO",
      color: "border-red-600 bg-red-50",
      accion: "Refuerzo operativo inmediato y revisión de capacidad",
    };
  }

  if (i.tendencia === "ESTABLE") {
    return {
      nivel: "MEDIO",
      color: "border-yellow-600 bg-yellow-50",
      accion: "Monitoreo continuo y ajustes menores",
    };
  }

  return {
    nivel: "BAJO",
    color: "border-green-600 bg-green-50",
    accion: "Mantener estrategia actual",
  };
}

/* ===============================
   IA Payload Builder (para Bedrock/Claude vía backend)
================================ */
function buildResumenPayload({
  anio,
  mesResumen,
  estadoGeneral,
  totalMunicipios,
  municipiosCreciente,
  municipiosEstables,
  municipiosEnRiesgo,
  topMejoraron,
  topCayeron,
  insights,
  resumen,
}) {
  const topUp = (topMejoraron || []).map((x) => ({
    municipio: x.municipio,
    porcentaje: x.porcentaje,
    delta: x.delta,
  }));

  const topDown = (topCayeron || []).map((x) => ({
    municipio: x.municipio,
    porcentaje: x.porcentaje,
    delta: x.delta,
  }));

  const riesgos = (resumen || [])
    .filter((r) => r.tendencia === "DECRECIENTE")
    .slice(0, 10)
    .map((r) => ({
      municipio: r.municipio,
      tendencia: r.tendencia,
      promedioVariacion: r.promedioVariacion,
      total: r.total,
    }));

  const insightsCortos = (insights || []).slice(0, 20).map((i) => ({
    municipio: i.municipio,
    tendencia: i.tendencia,
    insight: i.insight,
    mejorMes: i.mejorMes,
    peorMes: i.peorMes,
    mesesEnRojo: i.mesesEnRojo,
  }));

  return {
    tipo: "RESUMEN_EJECUTIVO_MENSUAL",
    anio,
    mes: mesResumen,
    mesNombre: nombreMes(mesResumen),
    estadoGeneral,
    kpis: {
      totalMunicipios,
      municipiosCreciente,
      municipiosEstables,
      municipiosEnRiesgo,
    },
    top: {
      mejoraron: topUp,
      cayeron: topDown,
    },
    riesgo: {
      municipios: riesgos,
    },
    insights: insightsCortos,
    instrucciones: {
      tono: "institucional_directivo",
      maxParrafos: 2,
      sinLenguajeTecnico: true,
      incluirRecomendaciones: true,
    },
  };
}

/* ===============================
   Resumen Local Premium (Fallback)
================================ */
function generarResumenLocal({
  anio,
  mesResumen,
  estadoGeneral,
  totalMunicipios,
  municipiosCreciente,
  municipiosEstables,
  municipiosEnRiesgo,
  topMejoraron,
  topCayeron,
  insights,
}) {
  const periodo = `${nombreMes(mesResumen)} de ${anio}`;

  const topUp = topMejoraron?.[0]
    ? `${topMejoraron[0].municipio} (${Number(topMejoraron[0].porcentaje) || 0}%)`
    : "N/D";

  const topDown = topCayeron?.[0]
    ? `${topCayeron[0].municipio} (${Number(topCayeron[0].porcentaje) || 0}%)`
    : "N/D";

  const accionesRiesgo =
    municipiosEnRiesgo > 0
      ? "Se recomienda reforzar capacidad operativa, revisar tiempos de atención y validar disponibilidad de insumos en municipios en riesgo."
      : "Se recomienda mantener la estrategia operativa actual y continuar el monitoreo mensual.";

  const highlight = (insights || [])
    .filter((i) => i.tendencia === "DECRECIENTE")
    .slice(0, 2)
    .map((i) => `• ${i.municipio}: ${i.insight}`)
    .join("\n");

  return (
    `Durante ${periodo}, el comportamiento general se clasifica como ${estadoGeneral}. ` +
    `Se evaluaron ${totalMunicipios} municipios: ${municipiosCreciente} en crecimiento, ` +
    `${municipiosEstables} estables y ${municipiosEnRiesgo} en riesgo.\n\n` +
    `Principales resultados: el mayor avance se observó en ${topUp}, mientras que la principal caída se registró en ${topDown}. ` +
    `${accionesRiesgo}\n` +
    (highlight ? `\nFocos de atención:\n${highlight}` : "")
  );
}
